import React from 'react';

export type SearchHistoryItem = {
  id: string;
  keyword: string;
  service: string;
  mode: 'single' | 'compare';
  compareService?: string | null;
  suggestionCount?: number;
  commonCount?: number;
  leftOnlyCount?: number;
  rightOnlyCount?: number;
  wordCount?: number;
  topSuggestions?: string[];
  createdAtMs?: number;
};

type Props = {
  items: SearchHistoryItem[];
  loading: boolean;
  onSelect: (item: SearchHistoryItem) => void;
  onDelete?: (item: SearchHistoryItem) => void;
  variant?: 'compact' | 'page';
};

const formatDate = (value?: number) => {
  if (!value) return '';

  return new Intl.DateTimeFormat('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const RecentSearches: React.FC<Props> = ({
  items,
  loading,
  onSelect,
  onDelete,
  variant = 'compact',
}) => {
  const isPage = variant === 'page';

  return (
    <section className={isPage ? 'rounded border border-gray-200 bg-white p-5 shadow-sm' : 'mx-auto mb-4 max-w-5xl px-6'}>
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className={isPage ? 'text-xl font-bold text-gray-900' : 'text-xs font-bold text-gray-500'}>
            最近の検索
          </h2>
          {isPage && (
            <p className="mt-1 text-sm text-gray-500">
              行を押すと、そのキーワードで再分析します。
            </p>
          )}
        </div>
        {loading && <span className="text-xs font-semibold text-gray-400">読み込み中...</span>}
      </div>
      <div className={isPage ? 'mt-4 space-y-2' : 'mt-2 flex gap-2 overflow-x-auto pb-1'}>
        {!loading && items.length === 0 && (
          <div className="w-full rounded border border-dashed border-gray-200 bg-white px-3 py-6 text-center text-sm text-gray-500">
            まだ検索履歴がありません
          </div>
        )}
        {items.map((item) => (
          <div
            key={item.id}
            className={
              isPage
                ? 'group flex items-stretch gap-2 rounded border border-gray-200 bg-white p-3 shadow-sm transition hover:border-blue-200 hover:bg-blue-50'
                : 'group min-w-[220px] rounded border border-gray-200 bg-white shadow-sm transition hover:border-blue-200 hover:bg-blue-50'
            }
          >
            <button
              type="button"
              onClick={() => onSelect(item)}
              className={isPage ? 'min-w-0 flex-1 text-left' : 'w-full px-3 py-2 text-left'}
            >
              <div className="flex items-start justify-between gap-2">
                <p className={isPage ? 'truncate text-base font-bold text-gray-900' : 'truncate text-sm font-bold text-gray-800'}>
                  {item.keyword}
                </p>
                <span className="shrink-0 rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                  {item.mode === 'compare' ? '比較' : '通常'}
                </span>
              </div>
              <div className={isPage ? 'mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500' : 'mt-1 flex items-center justify-between gap-2 text-[11px] text-gray-500'}>
                <span className="truncate">
                  {item.mode === 'compare'
                    ? `${item.service} vs ${item.compareService || '-'}`
                    : item.service}
                </span>
                <span className="shrink-0">{formatDate(item.createdAtMs)}</span>
                {isPage && item.wordCount !== undefined && (
                  <span>抽出語 {item.wordCount}</span>
                )}
              </div>
              <p className={isPage ? 'mt-2 text-sm font-semibold text-blue-600' : 'mt-1 text-[11px] font-semibold text-blue-600'}>
                {item.mode === 'compare'
                  ? `共通 ${item.commonCount || 0} / 合計 ${item.suggestionCount || 0}`
                  : `${item.suggestionCount || 0}語`}
              </p>
              {isPage && item.topSuggestions && item.topSuggestions.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {item.topSuggestions.slice(0, 6).map((suggestion) => (
                    <span
                      key={suggestion}
                      className="rounded bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600"
                    >
                      {suggestion}
                    </span>
                  ))}
                </div>
              )}
            </button>
            {isPage && onDelete && (
              <button
                type="button"
                onClick={() => onDelete(item)}
                className="self-center rounded border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
              >
                削除
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default RecentSearches;
